import React, { useCallback, useEffect, useState } from "react";

import { getUserAuthorizedInterfaces } from "@keepeek/api-client";
import { dayjs, useCurrentUi, useCurrentUser } from "@keepeek/commons";
import { KButton } from "@keepeek/refront-components";
import { Alert, Box, Button, Container, Paper, Typography } from "@mui/material";
import { Field, getUiOptions } from "@rjsf/utils";
import Axios from "axios";
import { FilePondErrorDescription, FilePondFile } from "filepond";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import { useSnackbar } from "notistack";
import { FilePond, registerPlugin } from "react-filepond";
import { useTranslation } from "react-i18next";
import { useRecoilState, useSetRecoilState } from "recoil";
import { v4 as uuidv4 } from "uuid";

import {
  apiStoreAtom,
  ApiStoreType,
  dataChangeState,
} from "../../../../providers/admin/config/atoms";
import { addNewValueInApiStoreMap } from "../../../../providers/admin/config/utils";
import { useFileResourceCreate } from "../../../../providers/resource/hooks/useFileResourceCreate";
import { useFileResourceReplace } from "../../../../providers/resource/hooks/useFileResourceReplace";
import logger from "../../../logger-utils";
import DialogVideo from "./components/DialogVideo";
import LoadingInformation from "./components/LoadingInformation";
import ResourceRenderer from "./components/ResourceRenderer";
import {
  API_RESOURCE_NATURE,
  KEEPICKER_VIDEO_CLIENT_ID,
  getResourceType,
  isAllowKeepicker,
  LoadingStatus,
  ResourceType,
} from "./utils";

import "filepond/dist/filepond.min.css";

type UISchemaType = {
  "sync:field"?: string;
  "sync:type"?: string;
  "sync:value"?: string;
  "sync:canceltype"?: string;
  "sync:cancelvalue"?: string;
};

const getValueFromSchema = (
  schema: UISchemaType | undefined,
  typeKey: string,
  valueKey: string,
  defaultValue: string,
): string => {
  if (schema?.[typeKey] === "date" && !!schema?.[valueKey]) {
    if (schema[valueKey] === "now") {
      return dayjs().utc().toISOString();
    }
    return dayjs(schema[valueKey]).utc().toISOString();
  }
  return schema?.[valueKey] ?? defaultValue;
};

/**
 * Custom field to display a resource selector in any admin page
 */
const CustomSelectResource: Field = function (props) {
  const { schema, uiSchema, formData, onChange } = props;
  const { t } = useTranslation();
  const { currentUi } = useCurrentUi();
  const resourceType: ResourceType = getResourceType(getUiOptions(uiSchema));
  const allowKeepicker = isAllowKeepicker(getUiOptions(uiSchema));
  const { currentUser } = useCurrentUser();
  registerPlugin(FilePondPluginFileValidateSize);
  const [resourceUploadState, setResourceUploadState] = useState<LoadingStatus>(LoadingStatus.NONE);
  const { enqueueSnackbar } = useSnackbar();
  const { createResource } = useFileResourceCreate();
  const { replaceResource } = useFileResourceReplace();
  const [apiStore, setApiStore] = useRecoilState<ApiStoreType>(apiStoreAtom);
  const key =
    uiSchema && uiSchema["api:destination"] && uiSchema["api:field"]
      ? `${uiSchema["api:destination"]}:${uiSchema["api:field"]}`
      : "";
  const [currentResourceUrl, setCurrentResourceUrl] = useState<string>(formData?.url);
  const [currentResourcePreviewUrl, setCurrentResourcePreviewUrl] = useState<string>("");
  const [currentResourceVideoPreviewUrl, setCurrentResourceVideoPreviewUrl] = useState<string>("");
  const [currentResourceId, setCurrentResourceId] = useState<string>(formData?.id);
  const [currentResourceOriginalFileHeight, setCurrentResourceOriginalFileHeight] =
    useState<string>("");
  const [currentResourceOriginalFileWidth, setCurrentResourceOriginalFileWidth] =
    useState<string>("");
  const [resourceSelectId, setResourceSelectId] = useState<string | undefined>(props.id);
  const setDataChange = useSetRecoilState(dataChangeState);
  const [files, setFiles] = useState<any>([]);

  const [isKeepickerInRealm, setIsKeepickerInRealm] = useState<boolean>(false);
  const checkKeepickerInRealm = useCallback(async () => {
    const res = await getUserAuthorizedInterfaces(Axios, {
      userId: currentUser?.id,
    });
    setIsKeepickerInRealm(
      res.data._embedded?.ui.some((ui) => ui.clientId === KEEPICKER_VIDEO_CLIENT_ID) ?? false,
    );
  }, [currentUser?.id]);

  useEffect(() => {
    if (currentUser?.id) {
      checkKeepickerInRealm();
    }
  }, [currentUser?.id, checkKeepickerInRealm]);

  useEffect(() => {
    if (!key && formData) {
      setCurrentResourceUrl(formData.url);
      setCurrentResourceId(formData.id);
      setCurrentResourcePreviewUrl(formData.previewUrl || "");
      setCurrentResourceVideoPreviewUrl(formData.videoPreviewUrl || "");
      setCurrentResourceOriginalFileHeight(formData.originalFileHeight || "");
      setCurrentResourceOriginalFileWidth(formData.originalFileWidth || "");
      if (resourceSelectId !== props.id) {
        setResourceUploadState(LoadingStatus.NONE);
        setResourceSelectId(props.id);
      }
    } else {
      if (
        currentUi &&
        currentUi?.gdprCurrentResources &&
        uiSchema &&
        uiSchema["api:destination"] &&
        uiSchema["api:field"]
      ) {
        const uiGdprResource = apiStore[key] ?? currentUi.gdprCurrentResources;
        setCurrentResourceUrl(uiGdprResource ?? "");
        if (uiGdprResource.lastIndexOf("/") > 0 && uiGdprResource.lastIndexOf(".") > 1) {
          setCurrentResourceId(
            uiGdprResource
              ? uiGdprResource.substring(
                  uiGdprResource.lastIndexOf("/") + 1,
                  uiGdprResource.lastIndexOf("."),
                )
              : "",
          );
        }
      }
    }
  }, [apiStore, currentUi, formData, key, props.id, resourceSelectId, uiSchema]);

  const handleSync = useCallback(
    ({ cancel = false }: { cancel?: boolean }) => {
      if (uiSchema?.["sync:field"]) {
        const value = getValueFromSchema(uiSchema as UISchemaType, "sync:type", "sync:value", "");
        const cancelValue = getValueFromSchema(
          uiSchema as UISchemaType,
          "sync:canceltype",
          "sync:cancelvalue",
          value,
        );

        addNewValueInApiStoreMap(
          uiSchema["sync:field"],
          cancel ? cancelValue : value,
          setApiStore,
          setDataChange,
        );
      }
    },
    [uiSchema, setApiStore, setDataChange],
  );

  const handleOnChange = useCallback(
    (value: {
      id: string;
      url: string;
      previewUrl?: string;
      videoPreviewUrl?: string;
      originalFileHeight?: string;
      originalFileWidth?: string;
      permalinks?: Array<{
        title: string;
        url: string;
      }>;
    }) => {
      onChange(value);
      handleSync({});
    },
    [handleSync, onChange],
  );

  async function handleAddFile(file: FilePondFile): Promise<{
    id: string;
    url: string;
    previewUrl?: string;
    videoPreviewUrl?: string;
    originalFileHeight?: string;
    originalFileWidth?: string;
    permalinks?: Array<{
      id?: string;
      title: string;
      description: string;
      url: string;
    }>;
  }> {
    handleSync({});
    if (!file) {
      return Promise.reject();
    }
    if (!currentResourceUrl) {
      const myUuid = uuidv4();
      let id = file.filenameWithoutExtension.replace(/[^a-zA-Z0-9]/g, "");
      id = id.concat("_").concat(myUuid.substring(0, 5));
      const url = await createResource(file.file, API_RESOURCE_NATURE, id);
      if (url) {
        setResourceUploadState(LoadingStatus.LOADED);
        enqueueSnackbar(t("admin:resource.create.success"), {
          variant: "success",
        });
        return { id, url };
      } else {
        setResourceUploadState(LoadingStatus.ERROR);
        return Promise.reject();
      }
    } else {
      const url = await replaceResource(file.file, API_RESOURCE_NATURE, currentResourceId);
      if (url) {
        setResourceUploadState(LoadingStatus.LOADED);
        enqueueSnackbar(t("admin:resource.replace.success"), {
          variant: "success",
        });
        return {
          id: currentResourceId,
          url,
          previewUrl: currentResourcePreviewUrl,
          originalFileHeight: currentResourceOriginalFileHeight,
          originalFileWidth: currentResourceOriginalFileWidth,
        };
      } else {
        setResourceUploadState(LoadingStatus.ERROR);
        return Promise.reject();
      }
    }
  }

  const handleResourceRemove = (): void => {
    if (key) {
      setCurrentResourceUrl("");
      addNewValueInApiStoreMap(key, "", setApiStore, setDataChange);
      setResourceUploadState(LoadingStatus.NONE);
      handleSync({ cancel: true });
    } else {
      handleOnChange({ id: "", url: "", originalFileHeight: "", originalFileWidth: "" });
    }
  };
  const hideResourceRemoveButton = getUiOptions(uiSchema).hideResourceRemoveButton === true;

  const [confirmation, setConfirmation] = useState(false);

  const [openKeepickerDialog, setOpenKeepickerDialog] = useState(false);

  if (uiSchema?.["ui:needconfirmation"] && !confirmation) {
    const confirmationLabel = t(uiSchema["ui:needconfirmation"]);
    return (
      <Container component={Paper} sx={{ padding: 1 }} maxWidth={false}>
        <Typography variant="h6">
          {schema.title ? t(schema.title) : t("admin:labels.translation.resource.label")}
        </Typography>
        <ResourceRenderer
          resourceType={resourceType}
          resourceUrl={currentResourceUrl}
          onResourceRemove={() => {}}
          hideResourceRemoveButton={true}
        />
        <Alert severity="error">
          <Typography sx={{ marginBottom: 2 }}>{confirmationLabel}</Typography>
          <KButton variant="contained" onClick={() => setConfirmation(true)}>
            {t("admin:configuration.rjsf.ui.confirmation.action.label")}
          </KButton>
        </Alert>
      </Container>
    );
  }

  return (
    <Container component={Paper} sx={{ padding: 1 }} maxWidth={false}>
      <Typography variant="h6">
        {schema.title ? t(schema.title) : t("admin:labels.translation.resource.label")}
      </Typography>
      {allowKeepicker ? (
        <Box sx={{ display: "flex", flexDirection: "row", gap: 6 }}>
          <Box sx={{ flexDirection: "column" }}>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              {t("admin:resource.video.label")}
            </Typography>
            <ResourceRenderer
              resourceType={resourceType}
              resourceUrl={currentResourceVideoPreviewUrl}
              onResourceRemove={handleResourceRemove}
              hideResourceRemoveButton={false}
            />
          </Box>
          {currentResourcePreviewUrl && (
            <Box sx={{ flexDirection: "column" }}>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {t("admin:resource.cover.label")}
              </Typography>
              <ResourceRenderer
                resourceType={ResourceType.IMAGE}
                resourceUrl={currentResourcePreviewUrl}
                onResourceRemove={() => {}}
                hideResourceRemoveButton={true}
              />
            </Box>
          )}
        </Box>
      ) : (
        <ResourceRenderer
          resourceType={resourceType}
          resourceUrl={currentResourceUrl}
          onResourceRemove={handleResourceRemove}
          hideResourceRemoveButton={hideResourceRemoveButton}
        />
      )}
      {!openKeepickerDialog && <LoadingInformation status={resourceUploadState} />}

      {allowKeepicker ? (
        <Box
          sx={{
            ...(resourceUploadState === LoadingStatus.LOADING && { display: "none" }),
            paddingTop: 2,
            paddingBottom: 2,
          }}
        >
          <Button
            variant="contained"
            onClick={() => {
              setFiles([]);
              setOpenKeepickerDialog(true);
            }}
          >
            {t("admin:resource.upload.title")}
          </Button>
          <DialogVideo
            open={openKeepickerDialog}
            onClose={() => {
              setOpenKeepickerDialog(false);
            }}
            resourceUploadState={resourceUploadState}
            setResourceUploadState={setResourceUploadState}
            files={files}
            setFiles={setFiles}
            widgetId={props.id}
            handleAddFile={handleAddFile}
            handleOnChange={handleOnChange}
            key={key}
            setApiStore={setApiStore}
            setDataChange={setDataChange}
            setCurrentResourceUrl={setCurrentResourceUrl}
            setCurrentResourceVideoPreviewUrl={setCurrentResourceVideoPreviewUrl}
            isKeepickerInRealm={isKeepickerInRealm}
          />
        </Box>
      ) : (
        <Box
          sx={{
            ...(resourceUploadState === LoadingStatus.LOADING && { display: "none" }),
            paddingBottom: 0.5,
          }}
        >
          <FilePond
            id={props.id}
            files={resourceUploadState === LoadingStatus.NONE ? undefined : files}
            onupdatefiles={(files) => setFiles(files)}
            onaddfilestart={() => setResourceUploadState(LoadingStatus.LOADING)}
            allowMultiple={false}
            name="files"
            maxFileSize={"50MB"}
            labelMaxFileSizeExceeded={
              t("admin:resource.upload.error.maxFileSizeExceeded") as string
            }
            labelMaxFileSize={t("admin:resource.upload.error.maxFileSize") as string}
            labelIdle={t("admin:resource.upload.label") as string}
            onaddfile={async (error: FilePondErrorDescription | null, file: FilePondFile) => {
              if (error === null) {
                const resource = await handleAddFile(file);
                if (resource) {
                  if (!key) {
                    handleOnChange({ id: resource.id, url: resource.url });
                  } else {
                    addNewValueInApiStoreMap(key, resource.url, setApiStore, setDataChange);
                    setCurrentResourceUrl(resource.url);
                  }
                }
              } else {
                setResourceUploadState(LoadingStatus.ERROR);
              }
            }}
            onerror={(status, file) => {
              logger.error(
                "Error while loading file ",
                file?.filename,
                " with error code ",
                status.code,
              );
            }}
            credits={false}
          />
        </Box>
      )}
    </Container>
  );
};

export default CustomSelectResource;
